import { useParams, useSearchParams } from "react-router-dom";
import {
  PaginationType,
  SetPaginationCountType,
} from "src/components/TablePaginationLimitChanger/hooks/useTablePaginationLimitChanger";
import { useSetPaginationCount } from "src/hooks/useSetPaginationCount";

import { DateRange } from "node_modules/react-day-picker/dist/esm";
import { useState } from "react";
import { SelectOptionsType } from "src/types";
import {
  convertDateRangeToParams,
  convertFilterToParams,
  convertOptionsToParams,
  convertStringToParams,
  getDateRangeParam,
  getFilterParam,
  getOptionParam,
  getStringParam,
} from "src/utils/convertToSearchParams";
import { useGetSalespersonTransactionList } from "../api/queries";
import { useGetAccessPermission } from "src/hooks/useGetAccessPermission";
import { AccessResource, ActionType } from "src/types/accessControl";
import { format } from "date-fns";
import { convertArrayParamsToString } from "src/utils/convertArrayParamsToString";

interface UseSalesPersonDetailTransactionListHandlerHandlerProps {
  pagination: PaginationType;
  setCount: SetPaginationCountType;
}

export interface SalespersonDetailTransactionListFilterType {
  businessOperation: SelectOptionsType;
}

export const salespersonDetailTransactionListFilterDefaultValue = {
  businessOperation: [],
};

export const salespersonDetailTransactionListSortOptions = [
  {
    label: "Latest Purchase",
    value: "latest-purchase",
  },
  {
    label: "Earliest Purchase",
    value: "earliest-purchase",
  },
  {
    label: "Most Total Amount",
    value: "most-total-amount",
  },
  {
    label: "Least Total Amount",
    value: "least-total-amount",
  },
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const salespersonDetailTransactionListSortOptionsValue = [
  "latest-purchase",
  "earliest-purchase",
  "most-total-amount",
  "least-total-amount",
] as const;

export type SalespersonDetailTransactionListSortValueType =
  (typeof salespersonDetailTransactionListSortOptionsValue)[number];

export const getSalespersonDetailTransactionListSortingParams = (
  type: SalespersonDetailTransactionListSortValueType
) => {
  let params = {
    sort_by: "",
    sort_order: "",
  };

  if (type === "latest-purchase") {
    params = {
      sort_by: "order_date",
      sort_order: "desc",
    };
  } else if (type === "earliest-purchase") {
    params = {
      sort_by: "order_date",
      sort_order: "asc",
    };
  } else if (type === "most-total-amount") {
    params = {
      sort_by: "total_payment",
      sort_order: "desc",
    };
  } else if (type === "least-total-amount") {
    params = {
      sort_by: "total_payment",
      sort_order: "asc",
    };
  }

  return params;
};

interface GetStockPurchaseExportSummariesProps {
  searchInputValue: string;
  dateFilterInputValue?: DateRange;
  filter: SalespersonDetailTransactionListFilterType;
  sort: SalespersonDetailTransactionListSortValueType;
  salespersonId: string;
}

const getStockPurchaseExportSummaries = ({
  searchInputValue,
  dateFilterInputValue,
  sort,
  filter,
  salespersonId,
}: GetStockPurchaseExportSummariesProps) => {
  return [
    {
      label: "Customer Name / Phone Number",
      value: searchInputValue !== "" ? searchInputValue : "All Stock Purchase",
      paramsKey: "qn",
      paramsValue: searchInputValue ? searchInputValue : "",
    },
    {
      label: "Transaction Start Date",
      value: dateFilterInputValue?.from
        ? format(dateFilterInputValue.from, "dd MMMM yyyy")
        : "All Date",
      paramsKey: "start_order_date",
      paramsValue: dateFilterInputValue?.from
        ? format(dateFilterInputValue.from, "yyyy-MM-dd")
        : "",
    },
    {
      label: "Transaction End Date",
      value: dateFilterInputValue?.to
        ? format(dateFilterInputValue.to, "dd MMMM yyyy")
        : "All Date",
      paramsKey: "end_order_date",
      paramsValue: dateFilterInputValue?.to
        ? format(dateFilterInputValue.to, "yyyy-MM-dd")
        : "",
    },
    {
      label: "Business Operation",
      value:
        filter.businessOperation.length > 0
          ? `${filter.businessOperation.map((psc) => psc?.label)}`
          : "All Business Operation",
      paramsKey: "business_operation_ids",
      paramsValue:
        filter.businessOperation.length > 0
          ? convertArrayParamsToString(
              filter.businessOperation.map((option) => option.value)
            )
          : "",
    },
    {
      label: "Sort By",
      value:
        salespersonDetailTransactionListSortOptions.find(
          (salespersonDetailTransactionListSortOption) =>
            salespersonDetailTransactionListSortOption.value === sort
        )?.label ?? "",
      paramsKey: "sort_by",
      paramsValue:
        getSalespersonDetailTransactionListSortingParams(sort)["sort_by"],
    },
    {
      label: "",
      value: "",
      paramsKey: "sort_order",
      paramsValue:
        getSalespersonDetailTransactionListSortingParams(sort)["sort_order"],
    },
    {
      label: "",
      value: "",
      paramsKey: "salesperson_id",
      paramsValue: salespersonId,
    },
  ];
};

const useSalespersonDetailTransactionListHandler = ({
  pagination,
  setCount,
}: UseSalesPersonDetailTransactionListHandlerHandlerProps) => {
  const { id } = useParams();

  const isAllowedBusinessOperation = useGetAccessPermission({
    resource: AccessResource["Master Data - Business Operation"],
    action: ActionType["Read"],
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const initialSearchInputValue = getStringParam(
    searchParams,
    "searchValue",
    ""
  );

  const initialDateRange = getDateRangeParam(
    searchParams,
    "customerTransactionDateFrom",
    "customerTransactionDateTo"
  );

  const initialSort = getOptionParam(
    searchParams,
    "sort",
    salespersonDetailTransactionListSortOptions.map((o) => o.value),
    salespersonDetailTransactionListSortOptions[0].value
  );

  const initialFilter =
    getFilterParam<SalespersonDetailTransactionListFilterType>(
      searchParams,
      "filter",
      salespersonDetailTransactionListFilterDefaultValue
    );

  const [dateFilterInputValue, setDateFilterInputValueState] = useState<
    DateRange | undefined
  >(initialDateRange);
  const [searchInputValue, setSearchInputValueState] = useState(
    initialSearchInputValue
  );
  const [sort, setSortState] = useState(initialSort);
  const [filter, setFilterState] =
    useState<SalespersonDetailTransactionListFilterType>(initialFilter);
  const [isShowExportSummaryModal, setIsShowExportSummaryModal] =
    useState(false);

  const setSearchInputValue = convertStringToParams(
    "searchValue",
    setSearchInputValueState,
    setSearchParams
  );

  const setDateFilterInputValue = convertDateRangeToParams(
    "customerTransactionDateFrom",
    "customerTransactionDateTo",
    setDateFilterInputValueState,
    setSearchParams
  );

  const setSort = convertOptionsToParams(
    "sort",
    setSortState as React.Dispatch<React.SetStateAction<any>>,
    setSearchParams
  );

  const setFilter =
    convertFilterToParams<SalespersonDetailTransactionListFilterType>(
      "filter",
      setFilterState,
      setSearchParams,
      (value) =>
        Object.values(value).some((v) =>
          Array.isArray(v) ? v.length > 0 : false
        )
    );

  const { data, isLoading } = useGetSalespersonTransactionList({
    ...pagination,
    id: id as string,
    ...(searchInputValue ? { qn: searchInputValue } : {}),
    sortBy: sort,
    startOrderDate: dateFilterInputValue?.from,
    endOrderDate: dateFilterInputValue?.to,
    ...filter,
  });

  const handleExportSummaryModal = () => setIsShowExportSummaryModal(true);
  const handleCloseExportSummaryModal = () =>
    setIsShowExportSummaryModal(false);

  useSetPaginationCount({ meta: data?.meta, setCount });

  const transactionList = data?.data;

  const exportSummaries = getStockPurchaseExportSummaries({
    searchInputValue,
    dateFilterInputValue,
    sort: sort as SalespersonDetailTransactionListSortValueType,
    filter,
    salespersonId: id || "",
  });

  return {
    isAllowedBusinessOperation,
    isLoading,
    searchInputValue,
    transactionList,
    totalCount: data?.meta.total ?? 0,
    setSearchInputValue,
    dateFilterInputValue,
    setDateFilterInputValue,
    sort,
    setSort,
    filter,
    setFilter,
    hasFilter:
      searchInputValue !== "" ||
      Object.values(filter).some((filter) =>
        Array.isArray(filter) ? filter.length > 0 : filter
      ) ||
      !!dateFilterInputValue,
    hasData: !isLoading && transactionList && transactionList.length > 0,
    isShowExportSummaryModal,
    handleExportSummaryModal,
    handleCloseExportSummaryModal,
    exportSummaries,
  };
};

export default useSalespersonDetailTransactionListHandler;
